import {
  getConversationMode,
  resolveSelectableMode,
} from '@/sections/insights/aiAssistant/config/storage';
import type { PersistableConversationMode } from '@/sections/insights/aiAssistant/types/conversation.types';

export interface ConvState {
  convId: string | null;
  mode: PersistableConversationMode;
  agent: string | null;
}

/**
 * Clamp the agent to the resolved mode. A non-agent mode must never carry an
 * agent — otherwise useMessageSending sends `activeAgent || mode` as a stale
 * agent against a UI showing 'query'. Single source of truth for the
 * agent/mode invariant: used by the derive (read) path AND the deliberate
 * write-through helpers, so in-memory state and storage can never drift.
 */
export function clampAgentToMode(
  mode: 'query' | 'agent',
  agent: string | null | undefined
): string | null {
  return mode === 'agent' ? (agent ?? null) : null;
}

/**
 * Derive the active chat conversation's mode + agent SYNCHRONOUSLY from its
 * conversationId. This is the single source of truth that makes the torn-state
 * class (mode lagging conversationId during a router transition) unrepresentable:
 * the returned mode is always a function of the id passed, never a free-standing
 * setState that can disagree with the URL.
 *
 * `resolveSelectableMode` is the single legacy-corruption heal point: a stale
 * 'auto' (the old per-conversation-mode bug) heals to 'query', and a stored
 * 'agent' downgrades to 'query' when agent mode is unavailable. The returned
 * `agent` is clamped to the resolved mode — it survives only when the resolved
 * mode is 'agent', so a healed-away mode can never leave a stale agent behind.
 *
 * The hunt ('auto') app-view is an orthogonal `isHuntView` flag, never a
 * per-conversation mode, so a fresh chat (no conversationId) always derives to
 * 'query'. The new-chat hunt intent is carried by `isHuntView`, not here — so
 * this is a pure function of (conversationId, storage, canUseAgentMode) with no
 * ref read, keeping the adjust-during-render call ref-free.
 */
export function deriveConvState(
  conversationId: string | null,
  opts: { canUseAgentMode: boolean }
): ConvState {
  if (conversationId) {
    const saved = getConversationMode(conversationId);
    const mode = resolveSelectableMode(saved, opts);
    return {
      convId: conversationId,
      mode,
      // Clamp the agent to the resolved mode: when the mode heals away from
      // 'agent' (agent mode unavailable, or a legacy 'auto' entry), the stored
      // agent must NOT survive — otherwise useMessageSending sends `activeAgent
      // || mode` as the stale agent against a UI that shows 'query'.
      agent: clampAgentToMode(mode, saved.agent),
    };
  }
  return { convId: null, mode: 'query', agent: null };
}
